import React from "react";
import styled from "styled-components";

import {
  HOURS_PER_DAY,
  isOvernightHour,
  LANE_WIDTH,
  LAYER,
  PX_PER_HOUR,
} from "../constants";
import { TShiftSection } from "../utils";

import { Shift } from "./Shift";

interface ShiftColumnProps {
  section: TShiftSection;
  totalHours: number;
  setSelectedShift: (shiftId: number) => void;
}

/**
 * The horizontal hour rules behind a column. Rendered by every category section
 * and by the filler that pads out the remaining width, so the grid always runs
 * edge to edge no matter how few categories are showing.
 */
export const HourGrid: React.FC<{ totalHours: number }> = ({ totalHours }) => (
  <>
    {Array.from({ length: totalHours }, (_, hour) => (
      <TimeBlock
        key={hour}
        isDayStart={hour % HOURS_PER_DAY === 0}
        isOvernight={isOvernightHour(hour % HOURS_PER_DAY)}
      />
    ))}
  </>
);

/** One category's column: the hour grid plus every shift tile in that category. */
export const ShiftsColumn: React.FC<ShiftColumnProps> = ({
  section,
  totalHours,
  setSelectedShift,
}) => (
  <CalendarColumn style={{ width: section.lanes * LANE_WIDTH }}>
    <HourGrid totalHours={totalHours} />

    {section.shifts.map((shift) => (
      <Shift key={shift.id} shift={shift} setSelectedShift={setSelectedShift} />
    ))}
  </CalendarColumn>
);

const CalendarColumn = styled.div`
  flex: none;
  position: relative;
  box-sizing: border-box;
  border-left: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};

  /* own stacking context, so a tile's z-index can only outrank its siblings
     and never the pinned gutter or header */
  z-index: ${LAYER.sectionColumn};
  isolation: isolate;
`;

const TimeBlock = styled.div<{ isDayStart: boolean; isOvernight: boolean }>`
  box-sizing: border-box;
  height: ${PX_PER_HOUR}px;
  border-top: 1px solid
    ${({ theme, isDayStart }) =>
      isDayStart
        ? theme.globalConstants.color.textSecondary
        : theme.globalConstants.color.backgroundGrayDark};
  background: ${({ theme, isOvernight }) =>
    isOvernight
      ? theme.globalConstants.color.backgroundGray
      : theme.globalConstants.color.transparent};
`;
